import React from 'react';
import { Button } from '../components/ui/button';
import { Award, Users, Target, TrendingUp, CheckCircle2 } from 'lucide-react';

const AboutUs = ({ onQuoteClick }) => {
  const team = [
    {
      name: "Bob Cooper",
      role: "Founder & CEO",
      bio: "Bob Cooper founded Cooper Equipment Rentals with a commitment to reliable equipment, affordable pricing, and service built around the needs of Southern California contractors.",
      image: "/team/bob-cooper.png",
    },
    {
      name: "Austin Cooper",
      role: "President & COO",
      bio: "Austin Cooper helps lead the next generation of Cooper Equipment Rentals, focusing on growth, operations, customer service, and keeping the company's family-owned values strong.",
      image: "/team/austin-cooper.png",
    },
  ];

  const timeline = [
    {
      year: "1999",
      title: "Company Founded",
      description:
        "Bob Cooper Sr. and Bob Cooper Jr. founded Cooper Equipment Rentals with 15 scissor lifts and a commitment to provide reliable equipment at affordable prices for Southern California contractors.",
    },
    {
      year: "2005",
      title: "Growing With Contractors",
      description:
        "Cooper Equipment Rentals grew from 15 lifts to 100 lifts by helping local contractors get the equipment they needed quickly, affordably, and reliably.",
    },
    {
      year: "2015",
      title: "Fleet Expansion",
      description:
        "By 2015, Cooper Equipment Rentals had grown to over 500 pieces of equipment, giving Southern California contractors more access to the lifts they needed, when they needed them.",
    },
    {
      year: "2017",
      title: "Family Leadership Expands",
      description:
        "Austin Cooper joined Cooper Equipment Rentals, and Trisha Cooper-Schuster became CFO, helping strengthen the family business for its next stage of growth.",
    },
    {
      year: "Today",
      title: "Still Growing, Still Family-Owned",
      description:
        "Today, Bob Cooper Jr., Austin Cooper, Trisha Cooper-Schuster, and other family members continue to grow Cooper Equipment Rentals while carrying forward the same family commitment to contractors.",
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1a2332] to-gray-800 text-white py-20 -mt-20 pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-black leading-tight mb-6">
              Built By Contractors, For Contractors
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Founded in 1999, Cooper Equipment Rentals was built by a family with deep roots in the trades. For
              over 25 years, we have served Southern California contractors with reliable equipment, affordable
              pricing, and service built around the needs of contractors.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Our mission is to provide Southern California contractors with reliable equipment, affordable
                pricing, and service built around the real needs of contractors.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                We believe renting equipment should be simple, clear, and dependable — with the right equipment,
                fair pricing, and a team that helps keep your job moving.
              </p>
              <div className="space-y-3">
                <div className="flex items-start">
                  <CheckCircle2 className="w-6 h-6 text-[#E28724] mr-3 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">
                    <span className="font-semibold">Reliable Equipment:</span> Well-maintained equipment that's ready to help get your job done.
                  </p>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="w-6 h-6 text-[#E28724] mr-3 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">
                    <span className="font-semibold">Transparent Pricing:</span> Clear pricing with no hidden fees, so you know what to expect before you rent.
                  </p>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="w-6 h-6 text-[#E28724] mr-3 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">
                    <span className="font-semibold">On-Time Service:</span> Delivery scheduled around your jobsite needs, so your crew is ready to get to work.
                  </p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600&fit=crop"
                alt="Cooper Equipment Fleet"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-xl text-gray-600">25+ years serving contractors across Southern California</p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-[#E28724]"></div>

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div
                  key={index}
                  className={`flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div
                    className={`w-full md:w-5/12 ${
                      index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:pl-12'
                    }`}
                  >
                    <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                      <div className="text-2xl font-black text-[#E28724] mb-2">{item.year}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>

                  <div className="hidden md:flex w-2/12 justify-center">
                    <div className="w-6 h-6 bg-[#E28724] rounded-full border-4 border-white shadow-lg z-10"></div>
                  </div>

                  <div className="hidden md:block w-5/12"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Team */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600">
              The Cooper family — leading the company with the same values it was founded on.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {team.map((member, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow"
                data-testid={`team-member-${index}`}
              >
                <div className="aspect-square overflow-hidden bg-gray-200">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-[#E28724] font-semibold mb-4">{member.role}</p>
                  <p className="text-gray-600 leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600">The principles that guide everything we do</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Quality First</h3>
              <p className="text-gray-600">
                Well-maintained equipment that's ready to help get your job done.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Customer Focus</h3>
              <p className="text-gray-600">
                We make it easy to order, schedule, and manage your rental from start to finish.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Transparency</h3>
              <p className="text-gray-600">
                Clear pricing with no hidden fees, so you know what to expect before you rent.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Continuous Improvement</h3>
              <p className="text-gray-600">
                We keep improving our fleet, service, and systems so we can serve customers better.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#1a2332] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-black text-[#E28724] mb-2">600+</div>
              <div className="text-lg text-gray-300">Units in Our Fleet</div>
            </div>
            <div>
              <div className="text-5xl font-black text-[#E28724] mb-2">25+</div>
              <div className="text-lg text-gray-300">Years in Business</div>
            </div>
            <div>
              <div className="text-5xl font-black text-[#E28724] mb-2">100%</div>
              <div className="text-lg text-gray-300 leading-tight">Final Pricing. No Add-ons. No surprises.</div>
            </div>
            <div>
              <div className="text-5xl font-black text-[#E28724] mb-2">On-Time</div>
              <div className="text-lg text-gray-300">Jobsite Delivery</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#E28724] to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Work with Us?</h2>
          <p className="text-xl mb-8 text-orange-100">
            Get a quote today and see why contractors across Southern California trust Cooper Equipment Rentals.
          </p>
          <Button
            onClick={onQuoteClick}
            size="lg"
            className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold"
          >
            Request a Quote
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
